import { useState, useEffect, type FormEvent } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate, Link } from '@tanstack/react-router';
import { Icon } from '../components/Icon.tsx';
import { meetingsApi, downloadAuthed, wapsignApi, type AgendaItemRow, type AgendaCategory, type Body } from '../features/meetings/api.ts';
import { MeetingForm, isoToMvtInput, mvtInputToIso, type MeetingFormValues } from '../features/meetings/MeetingForm.tsx';
import { MeetingRecipientsPanel } from '../features/meetings/MeetingRecipientsPanel.tsx';
// Temporarily hidden at user request — re-enable with the MeetingNumbersPanel block below.
// import { MeetingNumbersPanel } from '../features/meetings/MeetingNumbersPanel.tsx';
import { yaumiyyaApi } from '../features/yaumiyya/api.ts';
import { useAuthStore } from '../store/auth.ts';
import { dv } from '../i18n/dv.ts';
import { formatDvDate, formatDvTime, meetingNumber } from '../lib/dvDate.ts';
import { displayName } from '../lib/utils.ts';
import { meetingStatusKey } from '../lib/meetingStatus.ts';
import { submitterRoles, writerRoles, presidentRole, vicePresidentRole, schedulerRoles, electedRoles } from '../lib/body.ts';
import type { Role } from '../store/auth.ts';
import { Dropdown } from '../components/Dropdown.tsx';

// Which categories a given role may file under (mirrors the backend rule):
// the body's voting members (president, vice, member) file ONLY under
// «councilors' matters»; everyone else files under anything EXCEPT that. Used
// for both the submit form (current user's role) and the inline edit form (the
// item OWNER's role). Keyed on the meeting's body so WDC voting roles behave
// like their council equivalents.
function allowedCategoriesForRole(role: Role | undefined, body: Body): AgendaCategory[] {
  const memberRole: Role = body === 'WDC' ? 'WDC_MEMBER' : 'MEMBER';
  return role === presidentRole(body) || role === vicePresidentRole(body) || role === memberRole
    ? ['COUNCIL_MEMBERS']
    : ['COUNCIL_ADMIN', 'GOVT_OFFICES', 'OTHER'];
}

// Round icon button for the four document cards' action rows («ލިޔެކިޔުންތައް»).
// Icon-only: the Dhivehi label moves into title + aria-label, so a card's
// actions read as one compact row instead of a stack of long text buttons.
// `tone` mirrors the text buttons it replaces — filled for the card's main
// action, tonal for the rest. `busy` swaps the glyph for a spinner (download).
function DocAction({
  icon, label, onClick, disabled = false, tone = 'tonal', busy = false, title,
}: {
  icon: string;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  tone?: 'filled' | 'tonal';
  busy?: boolean;
  title?: string;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title ?? label}
      aria-label={label}
      className={`btn-icon ${tone === 'filled' ? 'btn-icon-filled' : 'btn-icon-tonal'}`}
    >
      {busy
        ? <span className="size-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
        : <Icon name={icon} size={20} />}
    </button>
  );
}

// Body-aware category label: WDC meetings use «committee» wording, council
// meetings use «council» wording. Mirrors backend SECTION_HEADINGS_BY_BODY.
function categoryLabel(category: string, body: Body): string {
  return (body === 'WDC' ? dv.agenda.categoriesWdc : dv.agenda.categories)[category]!;
}

// ---------------------------------------------------------------------------
// ItemCard is declared at module scope so it is never remounted due to a parent
// re-render (e.g. the 15 s polling refetch). All state it needs is passed as
// props. Previously it was an inner function of MeetingDetail, which caused
// React to treat it as a new component type on every render, unmounting and
// remounting every card and dropping focus from the reject-reason input.
// ---------------------------------------------------------------------------
// Inline edit form for a single PENDING item. Module-scope + only mounted while
// that item is being edited, so its local state initialises cleanly from the
// item each time editing opens. Category options come from the ITEM OWNER's role.
interface EditItemFormProps {
  item: AgendaItemRow;
  allowedCategories: AgendaCategory[];
  body: Body;
  pending: boolean;
  onSave: (id: string, form: FormData) => void;
  onCancel: () => void;
}

function EditItemForm({ item, allowedCategories, body, pending, onSave, onCancel }: EditItemFormProps): JSX.Element {
  const [title, setTitle] = useState(item.title);
  const [description, setDescription] = useState(item.description ?? '');
  const [category, setCategory] = useState<AgendaCategory>(
    allowedCategories.includes(item.category) ? item.category : allowedCategories[0]!,
  );
  // Ids of existing attachments the user has toggled for removal.
  const [removeIds, setRemoveIds] = useState<Set<string>>(new Set());
  const [files, setFiles] = useState<FileList | null>(null);

  function toggleRemove(id: string): void {
    setRemoveIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function submit(e: FormEvent): void {
    e.preventDefault();
    const form = new FormData();
    form.set('title', title);
    form.set('description', description);
    form.set('category', category);
    if (removeIds.size > 0) form.set('removeAttachmentIds', Array.from(removeIds).join(','));
    Array.from(files ?? []).forEach((f) => form.append('attachments', f));
    onSave(item.id, form);
  }

  return (
    <form className="space-y-3 ps-[3.75rem]" onSubmit={submit}>
      {item.status === 'APPROVED' && (
        <p className="rounded-2xl bg-[hsl(var(--warning-container))] px-3 py-2 text-sm text-[hsl(var(--on-warning-container))]">
          {dv.agenda.resubmitHint}
        </p>
      )}
      <input
        required
        placeholder={dv.agenda.itemTitle}
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        className="input"
      />
      <textarea
        placeholder={dv.agenda.description}
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        rows={3}
        className="textarea"
      />
      <div className="space-y-1">
        <label className="field-label">{dv.agenda.category}</label>
        {allowedCategories.length === 1 ? (
          <p className="rounded-2xl bg-[hsl(var(--surface-container-low))] px-3 py-2 text-sm">
            {categoryLabel(allowedCategories[0]!, body)}
          </p>
        ) : (
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value as AgendaCategory)}
            className="select"
          >
            {allowedCategories.map((c) => (
              <option key={c} value={c}>{categoryLabel(c, body)}</option>
            ))}
          </select>
        )}
      </div>
      {item.attachments.length > 0 && (
        <div className="space-y-1">
          <label className="field-label">{dv.agenda.existingAttachments}</label>
          <div className="flex flex-wrap gap-2">
            {item.attachments.map((a) => {
              const removed = removeIds.has(a.id);
              return (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => toggleRemove(a.id)}
                  className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs transition-colors ${
                    removed
                      ? 'bg-[hsl(var(--error-container))] text-[hsl(var(--destructive))] line-through'
                      : 'bg-[hsl(var(--surface-container-low))] text-foreground/80 hover:bg-[hsl(var(--surface-high))]'
                  }`}
                  dir="ltr"
                >
                  <Icon name="attach_file" size={14} className="shrink-0" />
                  {a.fileName}
                  <Icon name="close" size={14} className="shrink-0" />
                </button>
              );
            })}
          </div>
        </div>
      )}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <label className="text-sm cursor-pointer">
          <span className="btn-secondary btn-sm me-2">{dv.agenda.addMoreFiles}</span>
          <input
            type="file"
            multiple
            className="hidden"
            accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx"
            onChange={(e) => setFiles(e.target.files)}
          />
          {files && files.length > 0 && (
            <span className="text-muted-foreground">{files.length} 📎</span>
          )}
        </label>
        <div className="flex gap-2">
          <button type="submit" disabled={pending} className="btn-primary btn-sm">
            {dv.agenda.saveItem}
          </button>
          <button type="button" onClick={onCancel} className="btn-secondary btn-sm">
            {dv.common.cancel}
          </button>
        </div>
      </div>
    </form>
  );
}

// Reschedule form — small standalone form for the reschedule dialog (an inline
// expanding card, same as the meeting-edit form's pattern above). Declared at
// module scope, like EditItemForm, so its local state initialises cleanly each
// time the dialog opens rather than being remounted mid-typing.
interface RescheduleFormProps {
  initialWhen: string; // datetime-local value (MVT wall-clock), from isoToMvtInput
  initialLocation: string;
  pending: boolean;
  errorMessage: string | null;
  onSubmit: (values: { when: string; location?: string }) => void;
  onCancel: () => void;
}

function RescheduleForm({ initialWhen, initialLocation, pending, errorMessage, onSubmit, onCancel }: RescheduleFormProps): JSX.Element {
  const [when, setWhen] = useState(initialWhen);
  const [location, setLocation] = useState(initialLocation);

  function submit(e: FormEvent): void {
    e.preventDefault();
    onSubmit({ when, location: location.trim() || undefined });
  }

  return (
    <form className="space-y-3" onSubmit={submit}>
      <div className="flex flex-wrap gap-3">
        <div className="space-y-1">
          <label className="field-label">{dv.meetings.rescheduleWhen}</label>
          <input
            required
            type="datetime-local"
            dir="ltr"
            value={when}
            onChange={(e) => setWhen(e.target.value)}
            className="input"
          />
        </div>
        <div className="flex-1 space-y-1">
          <label className="field-label">{dv.meetings.rescheduleLocation}</label>
          <input value={location} onChange={(e) => setLocation(e.target.value)} className="input" />
        </div>
      </div>
      {errorMessage && <p className="text-sm text-destructive">{errorMessage}</p>}
      <div className="flex items-center gap-2">
        <button type="submit" disabled={pending} className="btn-primary">{dv.meetings.rescheduleSubmit}</button>
        <button type="button" onClick={onCancel} className="btn-ghost">{dv.common.cancel}</button>
      </div>
    </form>
  );
}

interface ItemCardProps {
  item: AgendaItemRow;
  index?: number;
  open: boolean;
  isPresident: boolean;
  currentUserId: string;
  body: Body;
  rejecting: string | null;
  reason: string;
  setRejecting: (id: string | null) => void;
  setReason: (r: string) => void;
  onDecide: (id: string, decision: 'APPROVED' | 'REJECTED', reason?: string) => void;
  onMove: (index: number, dir: -1 | 1) => void;
  decidePending: boolean;
  reorderPending: boolean;
  // Inline-edit state is lifted to MeetingDetail (like rejecting/reason) so the
  // module-scope ItemCard isn't remounted by the polling refetch and keeps focus.
  editingId: string | null;
  onStartEdit: (id: string) => void;
  onCancelEdit: () => void;
  onSaveEdit: (id: string, form: FormData) => void;
  onRemove: (id: string) => void;
  editPending: boolean;
  removePending: boolean;
}

function ItemCard({
  item, index, open, isPresident, currentUserId, body,
  rejecting, reason, setRejecting, setReason,
  onDecide, onMove, decidePending, reorderPending,
  editingId, onStartEdit, onCancelEdit, onSaveEdit, onRemove, editPending, removePending,
}: ItemCardProps): JSX.Element {
  // An item is editable only by the people involved in its submission: the
  // attributed member (submittedBy) OR whoever entered it (enteredById — the
  // SG/president for an on-behalf item). No unrelated president/SG can edit
  // another member's self-submission.
  const isOwner = currentUserId === item.submittedBy.id ||
    (!!item.enteredById && currentUserId === item.enteredById);
  // PENDING items edit in place; an already-passed (APPROVED) item can still be
  // edited by its owner — doing so sends it back to the president for approval.
  // REJECTED/finalized stay locked. Withdrawing (remove) is PENDING-only.
  const canEdit = open && isOwner && (item.status === 'PENDING' || item.status === 'APPROVED');
  const canRemove = open && isOwner && item.status === 'PENDING';
  const editing = editingId === item.id;
  // Category on edit is constrained to the ITEM OWNER's role (not the editor's),
  // so a president editing a member's item still stays under COUNCIL_MEMBERS.
  const editAllowedCategories = allowedCategoriesForRole(item.submittedBy.role, body);
  // Display marker: approved items show their agenda order number in a circular
  // chip; pending/rejected items show a quiet bullet so they don't read as final.
  const marker = item.orderIndex != null ? String(item.orderIndex) : '·';
  // Whether the bottom action bar has anything to show for this viewer/state.
  const canDecide = open && isPresident && item.status === 'PENDING';
  const canMove = open && isPresident && item.status === 'APPROVED' && index !== undefined;
  const hasActions = canDecide || canEdit || canRemove || canMove;
  return (
    <div className="space-y-3 p-4 sm:px-5">
      {/* Content uses the full card width — actions live in a bottom bar below. */}
      <div className="flex items-start gap-4">
        <span
          className={`mt-0.5 grid size-11 shrink-0 place-items-center rounded-full text-sm font-semibold tabular ${
            item.orderIndex != null
              ? 'bg-[hsl(var(--secondary-container))] text-[hsl(var(--on-secondary-container))]'
              : 'bg-[hsl(var(--surface-container-highest))] text-muted-foreground'
          }`}
        >
          {marker}
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-medium leading-snug">
            {item.title}
          </p>
          {item.description && (
            <p className="mt-1 text-sm text-muted-foreground">{item.description}</p>
          )}
          <p className="mt-1.5 text-xs text-muted-foreground">
            <span className="text-foreground/70">{displayName(item.submittedBy)}</span>
            <span className="mx-1.5 text-border">·</span>
            {item.submittedBy.designationDv ?? dv.roles[item.submittedBy.role]}
            <span className="mx-1.5 text-border">·</span>
            {categoryLabel(item.category, body)}
          </p>
          {/* Provenance shown on every item: who entered it into the system and
              when. For a self-submission that's the submitter; for an on-behalf
              item it's the enterer (SG/president). */}
          <p className="mt-1 text-xs text-muted-foreground">
            {dv.agenda.onBehalfBy}: {item.enteredByName ?? displayName(item.submittedBy)}
            <span className="mx-1.5 text-border">·</span>
            {formatDvDate(item.createdAt)} {formatDvTime(item.createdAt)}
          </p>
          {item.status === 'REJECTED' && item.rejectReason && (
            <p className="mt-1 text-xs text-destructive">
              {dv.agenda.rejectReason}: {item.rejectReason}
            </p>
          )}
        </div>
      </div>
      {!editing && item.attachments.length > 0 && (
        <div className="flex flex-wrap gap-2 ps-[3.75rem]">
          {item.attachments.map((a) => (
            <button
              key={a.id}
              onClick={() => void downloadAuthed(meetingsApi.attachmentUrl(a.id), a.fileName)}
              title={a.fileName}
              className="inline-flex max-w-full min-w-0 items-center gap-1.5 rounded-full bg-[hsl(var(--surface-container-low))] px-3 py-1.5 text-xs text-foreground/80 transition-colors hover:bg-[hsl(var(--surface-high))]"
              dir="ltr"
            >
              <Icon name="attach_file" size={14} className="shrink-0" />
              <span className="truncate">{a.fileName}</span>
            </button>
          ))}
        </div>
      )}
      {editing && (
        <EditItemForm
          item={item}
          allowedCategories={editAllowedCategories}
          body={body}
          pending={editPending}
          onSave={onSaveEdit}
          onCancel={onCancelEdit}
        />
      )}
      {/* Bottom action bar — keeps the content above full-width. */}
      {!editing && hasActions && (
        <div className="flex flex-wrap items-center gap-2 border-t border-black/[0.05] pt-3">
          {open && isPresident && item.status === 'PENDING' && (
            <>
              <button
                onClick={() => onDecide(item.id, 'APPROVED')}
                disabled={decidePending}
                className="btn-primary btn-sm"
              >
                {dv.agenda.approve}
              </button>
              <button
                onClick={() => setRejecting(item.id)}
                className="btn-danger btn-sm"
              >
                {dv.agenda.reject}
              </button>
            </>
          )}
          {canEdit && (
            <button
              onClick={() => onStartEdit(item.id)}
              className="btn-secondary btn-sm"
            >
              {dv.agenda.edit}
            </button>
          )}
          {canRemove && (
            <button
              onClick={() => { if (window.confirm(dv.agenda.removeConfirm)) onRemove(item.id); }}
              disabled={removePending}
              className="btn-danger btn-sm"
            >
              {dv.agenda.remove}
            </button>
          )}
          {open && isPresident && item.status === 'APPROVED' && index !== undefined && (
            <div className="ms-auto flex gap-1">
              <button
                aria-label={dv.agenda.moveUp}
                onClick={() => onMove(index, -1)}
                disabled={reorderPending}
                className="grid size-11 place-items-center rounded-full bg-[hsl(var(--secondary-container))] text-[hsl(var(--on-secondary-container))] transition-colors hover:brightness-95 disabled:opacity-40"
              >
                <Icon name="arrow_upward" size={20} />
              </button>
              <button
                aria-label={dv.agenda.moveDown}
                onClick={() => onMove(index, 1)}
                disabled={reorderPending}
                className="grid size-11 place-items-center rounded-full bg-[hsl(var(--secondary-container))] text-[hsl(var(--on-secondary-container))] transition-colors hover:brightness-95 disabled:opacity-40"
              >
                <Icon name="arrow_downward" size={20} />
              </button>
            </div>
          )}
        </div>
      )}
      {rejecting === item.id && (
        <form
          className="flex gap-2 ps-[3.75rem]"
          onSubmit={(e) => {
            e.preventDefault();
            if (reason.trim()) onDecide(item.id, 'REJECTED', reason.trim());
          }}
        >
          <input
            autoFocus
            required
            placeholder={dv.agenda.rejectReasonRequired}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="input flex-1"
          />
          <button
            type="submit"
            disabled={decidePending}
            className="btn-danger btn-sm"
          >
            {dv.agenda.reject}
          </button>
          <button
            type="button"
            onClick={() => { setRejecting(null); setReason(''); }}
            className="btn-secondary btn-sm"
          >
            {dv.common.cancel}
          </button>
        </form>
      )}
    </div>
  );
}

export function MeetingDetail(): JSX.Element {
  const { meetingId } = useParams({ from: '/meetings/$meetingId' });
  const qc = useQueryClient();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const invalidate = () => void qc.invalidateQueries({ queryKey: ['meeting', meetingId] });

  // --- submit form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [files, setFiles] = useState<FileList | null>(null);
  // The allowed categories depend on the meeting's body (computed after the
  // meeting loads, below); the form's category state starts on the safe
  // councilors' default and is normalised once the body is known.
  const [category, setCategory] = useState<AgendaCategory>('COUNCIL_MEMBERS');
  // Submit-on-behalf: SG/president may attribute the item to another elected
  // member of THIS meeting's body. '' means self-submit (unchanged behaviour).
  const [onBehalfOfId, setOnBehalfOfId] = useState('');

  // --- decision state
  const [rejecting, setRejecting] = useState<string | null>(null);
  const [reason, setReason] = useState('');

  // --- inline edit state (lifted so module-scope ItemCard isn't remounted)
  const [editingId, setEditingId] = useState<string | null>(null);

  // --- transfer-items panel state (defer approved items to another meeting)
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferTarget, setTransferTarget] = useState('');
  const [transferIds, setTransferIds] = useState<Set<string>>(new Set());

  // Mutations are declared BEFORE the query so that refetchInterval can
  // reference their isPending flags to pause polling while a mutation is in
  // flight (prevents stale-response overwrites and unnecessary network traffic).
  const submit = useMutation({
    mutationFn: () => {
      const form = new FormData();
      form.set('title', title);
      if (description) form.set('description', description);
      form.set('category', category);
      if (onBehalfOfId) form.set('onBehalfOfId', onBehalfOfId);
      Array.from(files ?? []).forEach((f) => form.append('attachments', f));
      return meetingsApi.submitItem(meetingId, form);
    },
    onSuccess: () => {
      setTitle(''); setDescription(''); setFiles(null);
      // Reset to the first category the submitter may file under for this body.
      // Keyed on the caller's own role (not effectiveRole) because the picker
      // also resets to "self" below.
      if (meeting) setCategory(allowedCategoriesForRole(user?.role, meeting.body)[0]!);
      setOnBehalfOfId('');
      invalidate();
    },
  });

  const decide = useMutation({
    mutationFn: (v: { id: string; decision: 'APPROVED' | 'REJECTED'; reason?: string }) =>
      meetingsApi.decide(v.id, v.decision, v.reason),
    onSuccess: () => { setRejecting(null); setReason(''); invalidate(); },
  });

  const reorder = useMutation({
    mutationFn: (itemIds: string[]) => meetingsApi.reorder(meetingId, itemIds),
    onSuccess: invalidate,
  });

  const updateItem = useMutation({
    mutationFn: (v: { id: string; form: FormData }) => meetingsApi.updateItem(v.id, v.form),
    onSuccess: () => { setEditingId(null); invalidate(); },
  });

  const removeItem = useMutation({
    mutationFn: (id: string) => meetingsApi.deleteItem(id),
    onSuccess: () => { setEditingId(null); invalidate(); },
  });

  // Scheduled meetings that can receive deferred items (excludes this one).
  const { data: scheduledTargets } = useQuery({
    queryKey: ['scheduled-meetings', meetingId],
    queryFn: () => meetingsApi.listScheduled(meetingId),
  });

  const transfer = useMutation({
    mutationFn: (v: { targetMeetingId: string; itemIds: string[] }) =>
      meetingsApi.transferItems(meetingId, v.targetMeetingId, v.itemIds),
    onSuccess: (_moved, v) => {
      setTransferOpen(false);
      setTransferTarget('');
      setTransferIds(new Set());
      invalidate();
      void qc.invalidateQueries({ queryKey: ['meeting', v.targetMeetingId] });
      void qc.invalidateQueries({ queryKey: ['scheduled-meetings', meetingId] });
    },
  });

  const finalize = useMutation({ mutationFn: () => meetingsApi.finalize(meetingId), onSuccess: invalidate });
  const reopen = useMutation({ mutationFn: () => meetingsApi.reopen(meetingId), onSuccess: invalidate });
  const reopenYaumiyya = useMutation({
    mutationFn: () => yaumiyyaApi.reopen(meetingId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['yaumiyya', meetingId] }),
  });
  const [editOpen, setEditOpen] = useState(false);

  const cancel = useMutation({
    mutationFn: () => meetingsApi.update(meetingId, { status: 'CANCELLED' }),
    onSuccess: invalidate,
  });

  const edit = useMutation({
    mutationFn: (values: MeetingFormValues) =>
      meetingsApi.update(meetingId, {
        title: values.title,
        scheduledAt: values.scheduledAt,
        location: values.location,
        meetingType: values.meetingType,
      }),
    onSuccess: () => {
      setEditOpen(false);
      invalidate();
      void qc.invalidateQueries({ queryKey: ['meetings'] });
    },
  });

  // Map the server's edit rejections to a clear Dhivehi message. The Edit
  // button is hidden once a meeting leaves SCHEDULED, but a stale tab could
  // still hit `meeting_locked`; year changes are rejected by design.
  function editErrorMessage(e: unknown): string {
    const code = (e as { response?: { data?: { error?: string } } } | undefined)?.response?.data?.error;
    if (code === 'year_change_not_allowed') return dv.meetings.editYearError;
    if (code === 'meeting_locked') return dv.meetings.editLockedError;
    return dv.common.error;
  }

  // Closing always clears any prior mutation error so a failed save doesn't
  // bleed into the next time the edit form is opened.
  function closeEdit(): void { edit.reset(); setEditOpen(false); }

  // Reschedule — post-finalize date/time (and optional location) change, distinct
  // from the pre-finalize `edit` above (which locks once the agenda finalizes).
  // Mirrors `editOpen`/`edit`'s pattern: an inline expanding card, not a modal.
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const reschedule = useMutation({
    mutationFn: (v: { when: string; location?: string }) =>
      meetingsApi.reschedule(meetingId, { scheduledAt: mvtInputToIso(v.when), location: v.location || undefined }),
    onSuccess: () => {
      setRescheduleOpen(false);
      invalidate();
      void qc.invalidateQueries({ queryKey: ['meetings'] });
    },
  });
  function closeReschedule(): void { reschedule.reset(); setRescheduleOpen(false); }
  // Map the server's reschedule rejections to a clear Dhivehi message — mirrors
  // `editErrorMessage`.
  function rescheduleErrorMessage(e: unknown): string {
    const code = (e as { response?: { data?: { error?: string } } } | undefined)?.response?.data?.error;
    if (code === 'year_change_not_allowed') return dv.meetings.rescheduleYearError;
    if (code === 'not_reschedulable') return dv.meetings.rescheduleNotAllowedError;
    return dv.common.error;
  }

  // Hard-delete (president only) — the meeting is gone afterwards, so refresh the
  // lists and leave the now-dead detail page for the meetings index.
  const remove = useMutation({
    mutationFn: () => meetingsApi.remove(meetingId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['meetings'] });
      void navigate({ to: '/meetings' });
    },
  });

  // Polling pauses while any mutation is in flight to avoid a stale refetch
  // response overwriting optimistic/in-progress state and to reduce flicker.
  const { data: meeting } = useQuery({
    queryKey: ['meeting', meetingId],
    queryFn: () => meetingsApi.get(meetingId),
    refetchInterval: (query) => {
      if (decide.isPending || reorder.isPending || submit.isPending || finalize.isPending ||
          updateItem.isPending || removeItem.isPending) return false;
      const data = query.state.data;
      // Fast-poll while the finalized agenda PDF is still rendering, so the UI
      // flips to "ready" (send-for-signing enabled, generating note cleared)
      // within ~2s of the worker finishing instead of up to a full 15s tick.
      if (data && data.status === 'AGENDA_FINALIZED' && data.agendaSignDocId == null &&
          data.agendaPassedAt == null && data.documents[0]?.finalized !== true) return 2_000;
      return 15_000;
    },
  });

  // Submit-on-behalf: only SG or the meeting body's president may attribute an
  // item to another member (mirrors the backend `resolveSubmitter` authorizer).
  const canSubmitOnBehalf = !!meeting && !!user &&
    (user.role === 'SECRETARY_GENERAL' || user.role === presidentRole(meeting.body));
  // Elected members of THIS meeting's body — the valid on-behalf targets.
  const { data: bodyMembers = [] } = useQuery({
    queryKey: ['council-members', meeting?.body],
    queryFn: () => yaumiyyaApi.councilMembers(meeting!.body),
    enabled: canSubmitOnBehalf && !!meeting,
  });
  const electedTargets = meeting ? bodyMembers.filter((m) => electedRoles(meeting.body).includes(m.role as never)) : [];
  // The role whose categories the picker should offer (target member if on-behalf).
  const effectiveRole = onBehalfOfId
    ? (electedTargets.find((m) => m.id === onBehalfOfId)?.role ?? user?.role)
    : user?.role;

  // Keep the submit-form category valid for THIS submitter's effective role +
  // the meeting's body. Without this, the state can stay on a value that isn't
  // one of the <select>'s options (e.g. the COUNCIL_MEMBERS default for a
  // SECRETARY_GENERAL, whose options are COUNCIL_ADMIN/GOVT_OFFICES/OTHER): the
  // browser then shows the first option while the stale, disallowed value is
  // what gets submitted → `category_not_allowed`. Mirrors the inline edit form's
  // guard. Also re-runs when `effectiveRole` changes (on-behalf target picked).
  useEffect(() => {
    if (!meeting || !user) return;
    const allowed = allowedCategoriesForRole(effectiveRole, meeting.body);
    setCategory((c) => (allowed.includes(c) ? c : allowed[0]!));
  }, [meeting, user, effectiveRole]);

  // Yaumiyya summary for the side card — only relevant once the agenda is final.
  const { data: yaumiyya } = useQuery({
    queryKey: ['yaumiyya', meetingId],
    queryFn: () => yaumiyyaApi.get(meetingId),
    enabled: meeting?.status === 'AGENDA_FINALIZED',
    refetchInterval: 30_000,
  });
  const [signError, setSignError] = useState(false);
  const [genericError, setGenericError] = useState(false);
  const [notReadyError, setNotReadyError] = useState(false);
  const [opening, setOpening] = useState(false);
  // Which document is currently downloading ('agenda' | 'yaumiyya' | null) — the
  // button shows a loading state until the file fetch finishes.
  const [downloading, setDownloading] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState(false);
  async function runDownload(key: string, url: string, fileName: string): Promise<void> {
    setDownloading(key);
    setDownloadError(false);
    try {
      await downloadAuthed(url, fileName);
    } catch {
      setDownloadError(true);
    } finally {
      setDownloading(null);
    }
  }
  // Only a genuine wapsign_account_not_found from the server means "no account".
  // Transient failures (expired token, timeout, 5xx) must NOT be reported as a
  // missing account — that misled testers into thinking valid accounts were gone.
  function isNoAccount(e: unknown): boolean {
    return (e as { response?: { data?: { error?: string } } })?.response?.data?.error === 'wapsign_account_not_found';
  }
  function reportSignError(e: unknown): void {
    const code = (e as { response?: { data?: { error?: string } } })?.response?.data?.error;
    // The finalized PDF renders asynchronously; a send before it lands returns
    // no_document/not_finalized. Show a "still generating" note, not a hard error.
    if (code === 'no_document' || code === 'not_finalized') { setNotReadyError(true); return; }
    if (isNoAccount(e)) setSignError(true); else setGenericError(true);
  }
  // Open WapSign for a given document via SSO handoff. In demo mode the
  // signDocId is a fake `demo-<uuid>` that never exists on the real WapSign
  // (see backend lib/wapsign.ts) — redirecting there would break the demo, so
  // no-op instead. This single guard covers every call site below (the 3
  // send-for-signing mutations + the 4 manual "open in WapSign" buttons);
  // callers already invalidate/refetch on success so the UI still reflects
  // the now-signed/passed state without the redirect.
  async function openInWapsign(signDocId: string): Promise<void> {
    if (signDocId.startsWith('demo-') || user?.isDemo) return;
    setOpening(true); setSignError(false); setGenericError(false); setNotReadyError(false);
    try {
      const url = await wapsignApi.handoff(`/documents/${signDocId}/edit`);
      window.location.href = url;
    } catch (e) { reportSignError(e); setOpening(false); }
  }
  // Send the finalized agenda for the president's signature, then hand off.
  const sendForSigning = useMutation({
    mutationFn: () => meetingsApi.sendForSigning(meetingId),
    onSuccess: (r) => { setSignError(false); setGenericError(false); setNotReadyError(false); void openInWapsign(r.signDocId); invalidate(); },
    onError: (e) => reportSignError(e),
  });
  // Send the rescheduled agenda for re-signing — mirrors sendForSigning above,
  // but targets the reschedule-specific sign/passed columns (acting president only).
  const rescheduleSendForSigning = useMutation({
    mutationFn: () => meetingsApi.rescheduleSendForSigning(meetingId),
    onSuccess: (r) => { setSignError(false); setGenericError(false); setNotReadyError(false); void openInWapsign(r.signDocId); invalidate(); },
    onError: (e) => reportSignError(e),
  });
  // Send the auto-generated ninmun for the acting president's signature, then
  // hand off into WapSign — mirrors sendForSigning (agenda).
  const sendNinmunForSigning = useMutation({
    mutationFn: () => yaumiyyaApi.sendNinmunForSigning(meetingId),
    onSuccess: (r) => {
      setSignError(false); setGenericError(false); setNotReadyError(false);
      void openInWapsign(r.signDocId);
      void qc.invalidateQueries({ queryKey: ['yaumiyya', meetingId] });
    },
    onError: (e) => reportSignError(e),
  });

  const sendHaazireeForSigning = useMutation({
    mutationFn: () => yaumiyyaApi.sendHaazireeForSigning(meetingId),
    onSuccess: (r) => {
      setSignError(false); setGenericError(false); setNotReadyError(false);
      void openInWapsign(r.signDocId);
      void qc.invalidateQueries({ queryKey: ['yaumiyya', meetingId] });
    },
    onError: (e) => reportSignError(e),
  });

  // Mark the meeting completed (read-only) / reopen it.
  const completeMeetingM = useMutation({
    mutationFn: () => meetingsApi.complete(meetingId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['meeting', meetingId] }),
  });
  const reopenCompletedM = useMutation({
    mutationFn: () => meetingsApi.reopenCompleted(meetingId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['meeting', meetingId] }),
  });

  if (!meeting || !user) return <p className="p-4 text-sm text-muted-foreground">{dv.common.loading}</p>;

  // All role gating is keyed on the meeting's body so a WDC meeting uses the
  // WDC role-sets and a council meeting the council role-sets (byte-identical
  // for COUNCIL).
  const body = meeting.body;
  // A completed meeting is read-only: every action below gates on `!isCompleted`
  // (the backend enforces it with 409 regardless). Reopen/mark-completed are the
  // only controls that stay live.
  const isCompleted = meeting.completedAt != null;
  // Strict elected body president (NOT the acting VP) — gates mark/reopen, same
  // rule the server uses.
  const electedPresident = user.role === presidentRole(body);
  const canComplete = !isCompleted && meeting.status !== 'CANCELLED' && (electedPresident || user.role === 'SECRETARY_GENERAL');
  // Categories the current submitter may file under for this body — keyed on
  // effectiveRole so an SG/president submitting on behalf sees the TARGET
  // member's categories, not their own.
  const allowedCategories: AgendaCategory[] = allowedCategoriesForRole(effectiveRole, body);
  // The acting chair wields the president's powers — resolved server-side via the
  // succession chain (president → VP → members, skipping leave). All president-
  // gated UI compares the viewer to it. `isDelegatedChair` (chairing but not the
  // real president) drives the delegation banner.
  const isChair = meeting.actingChairId === user.id;
  const isPresident = isChair && !isCompleted;
  const isDelegatedChair = isChair && user.role !== presidentRole(body);
  // Submitters of this body's agenda items (president, vice, member + SG).
  const canSubmit = submitterRoles(body).includes(user.role) && !isCompleted;
  // Writers of this body's yaumiyya (body elected + SG + Bureau).
  const isWriter = writerRoles(body).includes(user.role) && !isCompleted;
  const yStatus = yaumiyya?.status ?? null;
  // Ninmun signing state (mirrors the agenda's). Only meaningful once the
  // yaumiyya is APPROVED and a ninmun document exists.
  const ninmunSignDocId = yaumiyya?.ninmunSignDocId ?? null;
  const ninmunPassed = yaumiyya?.ninmunPassedAt != null;
  const ninmunSigning = ninmunSignDocId != null && !ninmunPassed;
  // Haaziree (attendance register) signing state — independent of the ninmun.
  const haazireeSignDocId = yaumiyya?.haazireeSignDocId ?? null;
  const haazireePassed = yaumiyya?.haazireePassedAt != null;
  const haazireeSigning = haazireeSignDocId != null && !haazireePassed;
  const agendaPassed = meeting.agendaPassedAt != null;
  const agendaSigning = meeting.agendaSignDocId != null && !agendaPassed;
  // Count of the document cards in the side column (max 4): agenda, yaumiyya,
  // The finalized agenda PDF renders asynchronously (a worker; up to a few
  // minutes for a large agenda). documents[0].finalized flips true only once
  // that PDF is on disk — until then send-for-signing would 409 (no_document).
  // Gate the send action + show a generating note on this signal.
  const agendaPdfReady = meeting.documents[0]?.finalized === true;
  const agendaPdfGenerating =
    meeting.status === 'AGENDA_FINALIZED' && !agendaSigning && !agendaPassed && !agendaPdfReady;
  const canFinalize = (isPresident || user.role === 'SECRETARY_GENERAL') && !isCompleted;
  const canCancel = schedulerRoles(body).includes(user.role) && !isCompleted;
  const open = meeting.status === 'SCHEDULED';
  const items = meeting.agendaItems;
  const pending = items.filter((i) => i.status === 'PENDING');
  const approved = items.filter((i) => i.status === 'APPROVED');
  const rejected = items.filter((i) => i.status === 'REJECTED');

  // Count of documents shown in the side column (agenda, yaumiyya, ninmun,
  // haaziree) — mirrors each card's own render condition so the pill on the
  // «ލިޔެކިޔުންތައް» section heading matches what's actually on screen. Each doc
  // counts once it exists; regenerating the agenda keeps it at 1 (not per-version).
  const documentCount =
    (meeting.documents.length > 0 ? 1 : 0) +
    (meeting.status === 'AGENDA_FINALIZED' || yaumiyya ? 1 : 0) +
    (yStatus === 'APPROVED' && yaumiyya?.ninmunDocument ? 1 : 0) +
    (yStatus === 'APPROVED' && yaumiyya?.haazireeDocument ? 1 : 0);

  // Reschedule: the original vs. rescheduled agenda documents, split by the
  // `rescheduled` flag (the merged download below covers both in one PDF, so
  // `documentCount` above stays at 1 for the agenda regardless of which exist).
  const originalDoc = meeting.documents.find((d) => !d.rescheduled) ?? null;
  const rescheduledDoc = meeting.documents.find((d) => d.rescheduled) ?? null;
  // Gate mirrors the server's `rescheduleMeeting` check exactly (services/meetings.ts):
  // agenda finalized + signed, meeting not completed/cancelled, yaumiyya not yet
  // APPROVED. Role gate reuses the same acting-president-or-SG combo as `canFinalize`.
  // Note: `meeting.status === 'AGENDA_FINALIZED'` already excludes CANCELLED,
  // so there is no separate `!== 'CANCELLED'` check (TS flags it as redundant).
  const canReschedule = (isPresident || user.role === 'SECRETARY_GENERAL')
    && meeting.status === 'AGENDA_FINALIZED' && meeting.agendaPassedAt != null
    && yStatus !== 'APPROVED' && !isCompleted;
  const rescheduledPassed = meeting.rescheduledAgendaPassedAt != null;
  const rescheduledSigning = meeting.rescheduledAgendaSignDocId != null && !rescheduledPassed;
  // The agenda card shows ONE status row for the CURRENT agenda: the rescheduled
  // re-issue once it exists, otherwise the original — never both.
  const latestAgendaDoc = rescheduledDoc ?? originalDoc;
  const latestAgendaPassed = rescheduledDoc ? rescheduledPassed : agendaPassed;

  function move(idx: number, dir: -1 | 1): void {
    // Guard against double-click firing while the previous reorder is still
    // in flight — a stale snapshot could corrupt the ordering.
    if (reorder.isPending) return;
    const ids = approved.map((i) => i.id);
    const j = idx + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[idx], ids[j]] = [ids[j]!, ids[idx]!];
    reorder.mutate(ids);
  }

  const sharedCardProps = {
    open,
    isPresident,
    currentUserId: user.id,
    body,
    rejecting,
    reason,
    setRejecting,
    setReason,
    onDecide: (id: string, decision: 'APPROVED' | 'REJECTED', r?: string) =>
      decide.mutate({ id, decision, reason: r }),
    onMove: move,
    decidePending: decide.isPending,
    reorderPending: reorder.isPending,
    editingId,
    onStartEdit: (id: string) => { setEditingId(id); setRejecting(null); },
    onCancelEdit: () => setEditingId(null),
    onSaveEdit: (id: string, form: FormData) => updateItem.mutate({ id, form }),
    onRemove: (id: string) => removeItem.mutate(id),
    editPending: updateItem.isPending,
    removePending: removeItem.isPending,
  };

  // Single overall meeting status (haalathu) — one value derived from the
  // meeting + agenda + yaumiyya state, used identically in the header and aside.
  const statusKey = meetingStatusKey(meeting);
  const statusLabel = dv.meetings.status[statusKey] ?? statusKey;
  const statusPillClass =
    statusKey === 'SCHEDULED'
      ? 'pill-active'
      : statusKey === 'YAUMIYYA_PROGRESS'
      ? 'pill-pending'
      : statusKey === 'CANCELLED'
      ? 'pill-neutral'
      : 'pill-success';

  return (
    <div className="page space-y-6">
      {/* Header band — masthead-style: meeting number + title, status, and the
          schedule as a clean meta row so the whole context scans at a glance. */}
      <header className="space-y-4 rounded-3xl bg-[hsl(var(--chrome))] p-5 lg:p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold leading-tight sm:text-3xl">
              {meeting.title}
            </h1>
          </div>
          <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
            <span className={`pill ${statusPillClass}`}>
              {statusLabel}
            </span>
            {/* Action buttons are icon-only (with aria-label + tooltip) so the
                header stays compact on mobile and never grows wider than the
                screen. */}
            {/* Mark completed — President or SG, while not completed/cancelled. */}
            {canComplete && (
              <button
                onClick={() => { if (window.confirm(dv.meetings.markCompletedConfirm)) completeMeetingM.mutate(); }}
                disabled={completeMeetingM.isPending}
                className="grid size-11 place-items-center rounded-full bg-primary text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-40"
                aria-label={dv.meetings.markCompleted}
                title={dv.meetings.markCompleted}
              >
                <Icon name="check_circle" size={20} />
              </button>
            )}
            {/* Reopen a completed meeting — elected president only. */}
            {isCompleted && electedPresident && (
              <button
                onClick={() => { if (window.confirm(dv.meetings.reopenCompletedConfirm)) reopenCompletedM.mutate(); }}
                disabled={reopenCompletedM.isPending}
                className="grid size-11 place-items-center rounded-full bg-[hsl(var(--secondary-container))] text-[hsl(var(--on-secondary-container))] transition-colors hover:brightness-95 disabled:opacity-40"
                aria-label={dv.meetings.reopenCompleted}
                title={dv.meetings.reopenCompleted}
              >
                <Icon name="restart_alt" size={20} />
              </button>
            )}
            {open && canCancel && (
              <button
                onClick={() => { edit.reset(); setEditOpen(true); }}
                className="grid size-11 place-items-center rounded-full bg-[hsl(var(--secondary-container))] text-[hsl(var(--on-secondary-container))] transition-colors hover:brightness-95"
                aria-label={dv.common.edit}
                title={dv.common.edit}
              >
                <Icon name="edit" size={20} />
              </button>
            )}
            {open && canCancel && (
              <button
                onClick={() => {
                  if (window.confirm(dv.meetings.cancelConfirm)) cancel.mutate();
                }}
                disabled={cancel.isPending}
                className="grid size-11 place-items-center rounded-full bg-[hsl(var(--error-container))] text-[hsl(var(--destructive))] transition-colors hover:brightness-95 disabled:opacity-40"
                aria-label={dv.meetings.cancelMeeting}
                title={dv.meetings.cancelMeeting}
              >
                <Icon name="block" size={20} />
              </button>
            )}
            {/* Private per-meeting notepad — an icon action in this row, sitting
                between the lifecycle actions and delete. Shown only once the
                agenda is SIGNED, the same moment the backend seeds the chair's
                script, so it appears exactly when there is something to read.
                Never role-gated: every org member has their own notepad, unlike
                the president/SG-only actions around it. */}
            {agendaPassed && (
              <Link
                to="/meetings/$meetingId/notes"
                params={{ meetingId }}
                className="grid size-11 place-items-center rounded-full bg-[hsl(var(--secondary-container))] text-[hsl(var(--on-secondary-container))] transition-colors hover:brightness-95"
                aria-label={dv.notes.entry}
                title={dv.notes.entry}
              >
                <Icon name="edit_note" size={20} />
              </Link>
            )}
            {/* The body's president may permanently delete the meeting (any
                status) — distinct from cancel, which keeps it on record. Gated
                on the real president role (not the acting VP) to match the
                server, which reserves deletion for the elected president. */}
            {user.role === presidentRole(body) && (
              <button
                onClick={() => {
                  if (window.confirm(dv.meetings.deleteConfirm)) remove.mutate();
                }}
                disabled={remove.isPending}
                className="grid size-11 place-items-center rounded-full bg-[hsl(var(--error-container))] text-[hsl(var(--destructive))] transition-colors hover:brightness-95 disabled:opacity-40"
                aria-label={dv.meetings.deleteMeeting}
                title={dv.meetings.deleteMeeting}
              >
                <Icon name="delete" size={20} />
              </button>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-muted-foreground">
          <span className="inline-flex items-center gap-1.5">
            <Icon name="calendar_today" size={16} className="shrink-0 text-foreground/40" />
            {formatDvDate(meeting.scheduledAt)}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Icon name="schedule" size={16} className="shrink-0 text-foreground/40" />
            <span className="tabular" dir="ltr">{formatDvTime(meeting.scheduledAt)}</span>
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Icon name="location_on" size={16} className="shrink-0 text-foreground/40" />
            {meeting.location}
          </span>
          <span className="pill pill-neutral">
            {dv.meetings.typeLabel[meeting.meetingType]}
          </span>
          <span className="pill pill-neutral">
            {dv.body[body]}
          </span>
        </div>
        {/* Reschedule banner — shown once the meeting has been rescheduled at
            least once (`originalScheduledAt` is set the first time). */}
        {meeting.originalScheduledAt && (
          <p className="flex items-center gap-1.5 text-xs text-[hsl(var(--on-warning-container))]">
            <Icon name="swap_horiz" size={15} className="shrink-0" />
            {dv.meetings.rescheduleBanner}
            {' — '}
            {dv.meetings.rescheduleFromTo
              .replace('{from}', formatDvDate(meeting.originalScheduledAt))
              .replace('{to}', formatDvDate(meeting.scheduledAt))}
          </p>
        )}
      </header>

      {editOpen && (
        <section className="space-y-3 rounded-3xl bg-[hsl(var(--chrome))] p-5 lg:p-6">
          <h2 className="status-label text-muted-foreground">{dv.meetings.editMeeting}</h2>
          <MeetingForm
            mode="edit"
            initial={{
              title: meeting.title,
              scheduledAt: meeting.scheduledAt,
              location: meeting.location,
              meetingType: meeting.meetingType,
              body: meeting.body,
              recipientIds: meeting.agendaRecipientIds,
            }}
            pending={edit.isPending}
            errorMessage={edit.isError ? editErrorMessage(edit.error) : null}
            onSubmit={(values) => edit.mutate(values)}
            onCancel={closeEdit}
          />
        </section>
      )}

      {/* Who the signed agenda is emailed to. Schedulers can adjust it until the
          agenda passes (matches the server gate); read-only for everyone else. */}
      <MeetingRecipientsPanel
        meetingId={meetingId}
        body={body}
        recipientIds={meeting.agendaRecipientIds}
        editable={schedulerRoles(body).includes(user.role) && !meeting.agendaPassedAt}
        agendaPassedAt={meeting.agendaPassedAt}
        agendaSend={meeting.agendaSend}
        onSaved={invalidate}
      />

      {/* Official document numbers (agenda / yaumiyya / ninmun). Schedulers can
          override just the sequence integer until each document is locked.
          TEMPORARILY HIDDEN at user request — re-enable by uncommenting this
          block and the MeetingNumbersPanel import. */}
      {/*
      <MeetingNumbersPanel
        meetingId={meetingId}
        numbering={meeting.numbering}
        editable={schedulerRoles(body).includes(user.role) && !isCompleted && meeting.status !== 'CANCELLED'}
        onSaved={invalidate}
      />
      */}

      {/* When the viewer is chairing by delegation (not the real president),
          make it explicit so the extra powers aren't a surprise. */}
      {isDelegatedChair && (
        <div className="flex items-center gap-3 rounded-3xl bg-[hsl(var(--warning-container))] px-5 py-3 text-sm text-[hsl(var(--on-warning-container))]">
          <Icon name="gavel" size={20} className="shrink-0" />
          {dv.president.actingNotice}
        </div>
      )}

      <div className="xl:grid xl:grid-cols-[1fr_320px] xl:gap-8 space-y-6 xl:space-y-0">
      {/* Main column — submit form + agenda item sections */}
      <div className="space-y-6">
      {/* Submit item form — visible to submitters while meeting is open */}
      {open && canSubmit && (
        <form
          className="space-y-3 rounded-3xl bg-[hsl(var(--chrome))] p-5 lg:p-6"
          onSubmit={(e) => { e.preventDefault(); submit.mutate(); }}
        >
          <h2 className="text-lg font-bold">{dv.agenda.submitItem}</h2>
          <input
            required
            placeholder={dv.agenda.itemTitle}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="input"
          />
          <textarea
            placeholder={dv.agenda.description}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            className="textarea"
          />
          {canSubmitOnBehalf && (
            <div className="space-y-1">
              <label className="field-label">{dv.agenda.onBehalfOf}</label>
              <Dropdown
                value={onBehalfOfId}
                placeholder={dv.agenda.onBehalfSelf}
                options={electedTargets.map((m) => ({ value: m.id, label: m.nameDv ?? m.name }))}
                onChange={(v) => setOnBehalfOfId(v)}
              />
            </div>
          )}
          <div className="space-y-1">
            <label className="field-label">{dv.agenda.category}</label>
            {allowedCategories.length === 1 ? (
              // President & members can only file under councilors' matters —
              // show it fixed rather than a single-option dropdown.
              <p className="rounded-2xl bg-[hsl(var(--surface-container-low))] px-3 py-2 text-sm">
                {categoryLabel(allowedCategories[0]!, body)}
              </p>
            ) : (
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value as AgendaCategory)}
                className="select"
              >
                {allowedCategories.map((c) => (
                  <option key={c} value={c}>{categoryLabel(c, body)}</option>
                ))}
              </select>
            )}
          </div>
          <div className="flex items-center justify-between gap-3">
            <label className="text-sm cursor-pointer">
              <span className="btn-secondary btn-sm me-2">
                {dv.agenda.addFiles}
              </span>
              <input
                type="file"
                multiple
                className="hidden"
                accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx"
                onChange={(e) => setFiles(e.target.files)}
              />
              {files && files.length > 0 && (
                <span className="text-muted-foreground">{files.length} 📎</span>
              )}
            </label>
            <button
              type="submit"
              disabled={submit.isPending}
              className="btn-primary"
            >
              {dv.common.submit}
            </button>
          </div>
          {submit.isError && (
            <p className="text-sm text-destructive">{dv.common.error}</p>
          )}
        </form>
      )}

      {/* Pending items — president review queue. Flagged with a cream rule so it
          reads as "needs a decision" rather than part of the final agenda. */}
      {pending.length > 0 && (
        <section className="space-y-2">
          <h2 className="section-heading text-lg font-bold">
            {dv.agenda.pendingSection}
            <span className="pill pill-pending shrink-0">{pending.length}</span>
          </h2>
          <div className="overflow-hidden rounded-3xl bg-[hsl(var(--chrome))] divide-y divide-black/[0.05] border-s-4 border-s-[hsl(var(--warning-container))]">
            {pending.map((i) => <ItemCard key={i.id} item={i} {...sharedCardProps} />)}
          </div>
        </section>
      )}

      {/* Approved items — the official agenda */}
      <section className="space-y-2">
        <h2 className="section-heading text-lg font-bold">
          {dv.agenda.approvedSection}
          <span className="pill pill-active shrink-0">{approved.length}</span>
        </h2>

        {/* Notes for items transferred OUT of this meeting to other scheduled
            meetings — keeps a visible record on the source meeting even though
            the items themselves have moved away (e.g. a no-quorum meeting). */}
        {(meeting.transfersOut ?? []).map((t) => {
          const targetLabel = t.toMeeting
            ? `${meetingNumber(t.toMeeting.year, t.toMeeting.sequence)} — ${t.toMeeting.title}`
            : '';
          return (
            <div key={t.id} className="rounded-3xl bg-[hsl(var(--warning-container))] p-4 text-[hsl(var(--on-warning-container))]">
              <div className="flex items-start gap-2">
                <Icon name="swap_horiz" size={18} className="mt-0.5 shrink-0" />
                <div className="space-y-1">
                  <p className="text-sm font-medium">
                    {t.toMeeting ? (
                      <Link
                        to="/meetings/$meetingId"
                        params={{ meetingId: t.toMeetingId }}
                        className="underline underline-offset-2 hover:no-underline"
                      >
                        {dv.agenda.transfer.note(t.count, targetLabel)}
                      </Link>
                    ) : (
                      dv.agenda.transfer.note(t.count, targetLabel)
                    )}
                  </p>
                  <p className="text-xs opacity-80">{formatDvDate(t.transferredAt)}</p>
                  {t.itemTitles.length > 0 && (
                    <ul className="mt-1 list-disc space-y-0.5 ps-5 text-xs opacity-90">
                      {t.itemTitles.map((title, idx) => (
                        <li key={idx}>{title}</li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          );
        })}

        <div className="overflow-hidden rounded-3xl bg-[hsl(var(--chrome))] divide-y divide-black/[0.05]">
          {approved.map((i, idx) => (
            <div key={i.id}>
              <ItemCard item={i} index={idx} {...sharedCardProps} />
            </div>
          ))}
          {approved.length === 0 && (
            <p className="p-6 text-center text-sm text-muted-foreground">{dv.agenda.empty}</p>
          )}
        </div>

        {/* Transfer (defer) approved items to another SCHEDULED meeting. Only
            for a meeting whose yaumiyya was FINALIZED as NO-QUORUM (approved +
            quorumMet false) — that is the sole case where approved items are
            deferred to a future meeting. Acting president or SG only. */}
        {approved.length > 0 && (isPresident || user.role === 'SECRETARY_GENERAL')
          && yaumiyya?.status === 'APPROVED' && yaumiyya.quorumMet === false && (
          (scheduledTargets?.length ?? 0) === 0 ? null : (
            <div className="space-y-3 rounded-3xl bg-[hsl(var(--chrome))] p-5 lg:p-6">
              {!transferOpen ? (
                <button
                  type="button"
                  className="btn-secondary btn-sm"
                  onClick={() => {
                    setTransferOpen(true);
                    setTransferIds(new Set(approved.map((i) => i.id)));
                    setTransferTarget(scheduledTargets?.[0]?.id ?? '');
                  }}
                >
                  {dv.agenda.transfer.button}
                </button>
              ) : (
                <div className="space-y-3">
                  <h3 className="text-sm font-medium">{dv.agenda.transfer.panelTitle}</h3>
                  <div className="space-y-1">
                    <label className="field-label">{dv.agenda.transfer.target}</label>
                    <select
                      value={transferTarget}
                      onChange={(e) => setTransferTarget(e.target.value)}
                      className="select"
                    >
                      {(scheduledTargets ?? []).map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.year}/{m.sequence} — {m.title}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-1">
                    <label className="field-label">{dv.agenda.transfer.selectItems}</label>
                    <div className="space-y-1.5">
                      {approved.map((i) => (
                        <label key={i.id} className="flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={transferIds.has(i.id)}
                            onChange={() => setTransferIds((prev) => {
                              const next = new Set(prev);
                              if (next.has(i.id)) next.delete(i.id); else next.add(i.id);
                              return next;
                            })}
                          />
                          <span>{i.title}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      disabled={transfer.isPending || !transferTarget || transferIds.size === 0}
                      className="btn-primary btn-sm"
                      onClick={() => {
                        const target = scheduledTargets?.find((m) => m.id === transferTarget);
                        const targetTitle = target ? `${target.year}/${target.sequence} — ${target.title}` : '';
                        if (window.confirm(dv.agenda.transfer.confirm(targetTitle))) {
                          transfer.mutate({ targetMeetingId: transferTarget, itemIds: Array.from(transferIds) });
                        }
                      }}
                    >
                      {dv.agenda.transfer.confirmButton}
                    </button>
                    <button
                      type="button"
                      className="btn-secondary btn-sm"
                      onClick={() => { setTransferOpen(false); setTransferIds(new Set()); }}
                    >
                      {dv.common.cancel}
                    </button>
                    {transfer.isSuccess && (
                      <span className="text-sm text-[hsl(var(--success))]">{dv.agenda.transfer.moved(transfer.data ?? 0)}</span>
                    )}
                    {transfer.isError && <span className="text-sm text-destructive">{dv.common.error}</span>}
                  </div>
                </div>
              )}
            </div>
          )
        )}
      </section>

      {/* Rejected items */}
      {rejected.length > 0 && (
        <section className="space-y-2">
          <h2 className="section-heading text-lg font-bold">
            {dv.agenda.rejectedSection}
            <span className="pill pill-neutral shrink-0">{rejected.length}</span>
          </h2>
          <div className="overflow-hidden rounded-3xl bg-[hsl(var(--chrome))] divide-y divide-black/[0.05] opacity-70">
            {rejected.map((i) => <ItemCard key={i.id} item={i} {...sharedCardProps} />)}
          </div>
        </section>
      )}
      </div>{/* end main column */}

      {/* Side column — two consistent sibling cards: AGENDA, then YAUMIYYA.
          Both share one template: heading · document row (when a doc exists) ·
          action row (border-t) · transient notes. The lifecycle actions that
          used to live in the status / next-action strips are folded in here. */}
      <aside className="space-y-6 xl:sticky xl:top-20 xl:self-start">
        {/* AGENDA section — «ލިޔެކިޔުންތައް» is the main heading over ALL four
            document cards (agenda, yaumiyya, ninmun, haaziree); the agenda card
            itself follows, always rendered (it's the core). */}
        <section className="space-y-2">
          {/* Column heading for the whole documents side — serif-bold + trailing
              rule + a count pill. */}
          <h2 className="section-heading text-lg font-bold">
            {dv.agenda.documentsSection}
            <span className="pill pill-active shrink-0">{documentCount}</span>
          </h2>
          <div className="space-y-3 rounded-3xl bg-[hsl(var(--chrome))] p-5 lg:p-6">
          {/* Card title — same style/placement as the yaumiyya, ninmun and
              haaziree cards so all four document cards read as a set. */}
          <h2 className="section-heading status-label !mb-0 text-muted-foreground">
            {dv.agenda.title}
          </h2>
          {/* Document row — status chip (signed/final) + mono generated date for
              the LATEST agenda only (the rescheduled re-issue if the meeting was
              rescheduled, otherwise the original — never both). */}
          {latestAgendaDoc && (
            <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
              {latestAgendaPassed ? (
                <span className="pill pill-success">{dv.agenda.signed}</span>
              ) : latestAgendaDoc.finalized ? (
                <span className="pill pill-success">{dv.agenda.finalVersion}</span>
              ) : (
                <span />
              )}
              <span className="mono-meta text-muted-foreground" dir="ltr">
                {formatDvDate(latestAgendaDoc.generatedAt)} {formatDvTime(latestAgendaDoc.generatedAt)}
              </span>
            </div>
          )}
          {/* Action row — state-driven agenda lifecycle actions. */}
          {(
            (open && canFinalize && approved.length > 0) ||
            (meeting.status === 'AGENDA_FINALIZED' && isPresident && !agendaPassed && !agendaSigning) ||
            agendaSigning ||
            (isPresident && (meeting.status === 'AGENDA_FINALIZED' || agendaSigning || agendaPassed) && yStatus !== 'APPROVED') ||
            (rescheduledDoc && !rescheduledPassed) ||
            rescheduledSigning ||
            canReschedule ||
            meeting.documents[0]
          ) && (
            <div className="flex flex-wrap items-center gap-2 border-t border-black/[0.05] pt-2.5">
              {open && canFinalize && approved.length > 0 && (
                <DocAction
                  icon="lock"
                  label={dv.agenda.finalize}
                  tone="filled"
                  onClick={() => { if (window.confirm(dv.agenda.finalizeConfirm)) finalize.mutate(); }}
                  disabled={finalize.isPending}
                />
              )}
              {meeting.status === 'AGENDA_FINALIZED' && isPresident && !agendaPassed && !agendaSigning && (
                <DocAction
                  icon="send"
                  label={dv.agenda.sendForSigning}
                  title={agendaPdfGenerating ? dv.agenda.pdfNotReady : undefined}
                  tone="filled"
                  onClick={() => sendForSigning.mutate()}
                  disabled={sendForSigning.isPending || opening || agendaPdfGenerating}
                />
              )}
              {/* Hidden in demo mode — nothing to open, signing already completed. */}
              {agendaSigning && !user.isDemo && (
                <DocAction
                  icon="open_in_new"
                  label={dv.agenda.openInWapsign}
                  tone="filled"
                  onClick={() => meeting.agendaSignDocId && void openInWapsign(meeting.agendaSignDocId)}
                  disabled={opening}
                />
              )}
              {/* Reopen the agenda for edit. Blocked once the yaumiyya is passed. */}
              {isPresident
                && (meeting.status === 'AGENDA_FINALIZED' || agendaSigning || agendaPassed)
                && yStatus !== 'APPROVED' && (
                <DocAction
                  icon="lock_open"
                  label={dv.agenda.reopen}
                  onClick={() => {
                    const msg = (agendaSigning || agendaPassed) ? dv.agenda.reopenSignedConfirm : dv.agenda.reopenConfirm;
                    if (window.confirm(msg)) reopen.mutate();
                  }}
                  disabled={reopen.isPending}
                />
              )}
              {/* Rescheduled agenda — send for re-signing (acting president
                  only, mirrors the server's route gate), then open/sign. */}
              {rescheduledDoc && !rescheduledSigning && !rescheduledPassed && isPresident && (
                <DocAction
                  icon="send"
                  label={dv.meetings.rescheduleSendForSigning}
                  tone="filled"
                  onClick={() => rescheduleSendForSigning.mutate()}
                  disabled={rescheduleSendForSigning.isPending || opening}
                />
              )}
              {/* Hidden in demo mode — nothing to open, signing already completed. */}
              {rescheduledSigning && !user.isDemo && (
                <DocAction
                  icon="open_in_new"
                  label={dv.agenda.openInWapsign}
                  tone="filled"
                  onClick={() => meeting.rescheduledAgendaSignDocId && void openInWapsign(meeting.rescheduledAgendaSignDocId)}
                  disabled={opening}
                />
              )}
              {(originalDoc || rescheduledDoc) && (
                <DocAction
                  icon="download"
                  label={downloading === 'agenda' ? dv.common.downloading : dv.common.download}
                  busy={downloading === 'agenda'}
                  onClick={() =>
                    void runDownload(
                      'agenda',
                      meetingsApi.agendaUrl(meeting.id),
                      `agenda-${meeting.year}-${meeting.sequence}.pdf`,
                    )
                  }
                  disabled={downloading === 'agenda'}
                />
              )}
              {/* Reschedule — acting president or SG, while the agenda is final +
                  signed and the yaumiyya hasn't been approved yet (mirrors the
                  server's `rescheduleMeeting` gate). */}
              {canReschedule && (
                <DocAction
                  icon="event_repeat"
                  label={dv.meetings.reschedule}
                  onClick={() => { reschedule.reset(); setRescheduleOpen(true); }}
                />
              )}
            </div>
          )}
          {/* Reschedule dialog — inline expanding, directly under the agenda card
              actions. Prefills the datetime-local with the meeting's MVT time. */}
          {rescheduleOpen && (
            <div className="border-t border-black/[0.05] pt-3">
              <h2 className="status-label text-muted-foreground">{dv.meetings.rescheduleTitle}</h2>
              <RescheduleForm
                initialWhen={isoToMvtInput(meeting.scheduledAt)}
                initialLocation={meeting.location}
                pending={reschedule.isPending}
                errorMessage={reschedule.isError ? rescheduleErrorMessage(reschedule.error) : null}
                onSubmit={(v) => reschedule.mutate(v)}
                onCancel={closeReschedule}
              />
            </div>
          )}
          {/* Transient notes — signing-in-progress / regenerating / errors. */}
          {downloadError && (
            <p className="text-xs text-destructive">{dv.common.error}</p>
          )}
          {agendaPdfGenerating && (
            <p className="text-xs text-[hsl(var(--warning))]">{dv.agenda.regenerating}</p>
          )}
          {agendaSigning && (
            <p className="text-xs text-[hsl(var(--warning))]">{dv.agenda.signingInProgress}</p>
          )}
          {rescheduledSigning && (
            <p className="text-xs text-[hsl(var(--warning))]">{dv.agenda.signingInProgress}</p>
          )}
          {signError && (
            <p className="text-xs text-destructive">{dv.agenda.wapsignNoAccount}</p>
          )}
          {notReadyError && (
            <p className="text-xs text-[hsl(var(--warning))]">{dv.agenda.pdfNotReady}</p>
          )}
          {genericError && (
            <p className="text-xs text-destructive">{dv.common.error}</p>
          )}
          </div>
        </section>

        {/* YAUMIYYA card — same template as the agenda card so the two read as a
            consistent pair. Rendered once the agenda is final (or a yaumiyya exists). */}
        {(meeting.status === 'AGENDA_FINALIZED' || yaumiyya) && (
          <section className="space-y-3 rounded-3xl bg-[hsl(var(--chrome))] p-5 lg:p-6">
            <h2 className="section-heading status-label !mb-0 text-muted-foreground">
              {dv.yaumiyya.title}
            </h2>
            {/* Document row — once signed (APPROVED), the signed chip + date. */}
            {yStatus === 'APPROVED' && yaumiyya?.documents?.[0] && (
              <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
                <span className="pill pill-success">{dv.yaumiyya.signed}</span>
                {yaumiyya.documents[0].generatedAt && (
                  <span className="mono-meta text-muted-foreground" dir="ltr">
                    {formatDvDate(yaumiyya.documents[0].generatedAt)} {formatDvTime(yaumiyya.documents[0].generatedAt)}
                  </span>
                )}
              </div>
            )}
            {/* Action row — write/view + signing/reopen/download. */}
            <div className="flex flex-wrap items-center gap-2 border-t border-black/[0.05] pt-2.5">
              {(() => {
                // Writers on a draft go in to write; everyone else views.
                const canWrite = isWriter && (!yaumiyya || yStatus === 'DRAFT');
                const label = canWrite ? dv.yaumiyya.write : dv.yaumiyya.title;
                return (
                  <Link
                    to="/meetings/$meetingId/yaumiyya"
                    params={{ meetingId }}
                    title={label}
                    aria-label={label}
                    className="btn-icon btn-icon-filled"
                  >
                    <Icon name={canWrite ? 'edit_note' : 'description'} size={20} />
                  </Link>
                );
              })()}
              {/* Open the yaumiyya signing document in WapSign. Hidden in demo
                  mode — nothing to open, signing already completed. */}
              {yStatus === 'PENDING_APPROVAL' && yaumiyya?.signDocId && !user.isDemo && (
                <DocAction
                  icon="open_in_new"
                  label={dv.yaumiyya.openInWapsign}
                  onClick={() => yaumiyya.signDocId && void openInWapsign(yaumiyya.signDocId)}
                  disabled={opening}
                />
              )}
              {/* Reopen the yaumiyya for edit + resend (WRITERS + PRESIDENT). */}
              {(isWriter || isPresident) && (yStatus === 'PENDING_APPROVAL' || yStatus === 'APPROVED') && (
                <DocAction
                  icon="lock_open"
                  label={dv.yaumiyya.reopen}
                  onClick={() => { if (window.confirm(dv.yaumiyya.reopenConfirm)) reopenYaumiyya.mutate(); }}
                  disabled={reopenYaumiyya.isPending}
                />
              )}
              {/* Once signed (APPROVED), surface the signed PDF for download. */}
              {yStatus === 'APPROVED' && yaumiyya?.documents?.[0] && (
                <DocAction
                  icon="download"
                  label={downloading === 'yaumiyya' ? dv.common.downloading : dv.common.download}
                  busy={downloading === 'yaumiyya'}
                  onClick={() => yaumiyya.documents[0] && void runDownload(
                    'yaumiyya',
                    yaumiyyaApi.documentUrl(yaumiyya.documents[0].id),
                    `yaumiyya-${meeting.year}-${meeting.sequence}.pdf`,
                  )}
                  disabled={downloading === 'yaumiyya'}
                />
              )}
            </div>
            {yStatus === 'PENDING_APPROVAL' && (
              <p className="text-xs text-[hsl(var(--warning))]">{dv.yaumiyya.signingInProgress}</p>
            )}
            {downloadError && (
              <p className="text-xs text-destructive">{dv.common.error}</p>
            )}
          </section>
        )}

        {/* Ninmun (decisions) — auto-generated when the yaumiyya is signed, then
            signed by the acting president via WapSign. Its own card, consistent
            with the agenda + yaumiyya cards. */}
        {yStatus === 'APPROVED' && yaumiyya?.ninmunDocument && (
          <section className="space-y-3 rounded-3xl bg-[hsl(var(--chrome))] p-5 lg:p-6">
            <h2 className="section-heading status-label !mb-0 text-muted-foreground">{dv.ninmun.title}</h2>
            <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
              {ninmunPassed ? <span className="pill pill-success">{dv.ninmun.signed}</span> : <span />}
              {yaumiyya.ninmunDocument?.generatedAt && (
                <span className="mono-meta text-muted-foreground" dir="ltr">
                  {formatDvDate(yaumiyya.ninmunDocument.generatedAt)} {formatDvTime(yaumiyya.ninmunDocument.generatedAt)}
                </span>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2 border-t border-black/[0.05] pt-2.5">
              {/* Not yet sent — SG/Bureau (or any writer) send it for signing,
                  then place the relevant councilor's signature box in WapSign. */}
              {!ninmunPassed && !ninmunSignDocId && isWriter && (
                <DocAction
                  icon="send"
                  label={dv.ninmun.sendForSigning}
                  tone="filled"
                  onClick={() => sendNinmunForSigning.mutate()}
                  disabled={sendNinmunForSigning.isPending || opening}
                />
              )}
              {/* Sent, not yet passed — open in WapSign to sign. Hidden in
                  demo mode — nothing to open, signing already completed. */}
              {ninmunSigning && !user.isDemo && (
                <DocAction
                  icon="open_in_new"
                  label={dv.ninmun.openInWapsign}
                  onClick={() => ninmunSignDocId && void openInWapsign(ninmunSignDocId)}
                  disabled={opening}
                />
              )}
              {/* Download the ninmun PDF as soon as it's generated (the signed
                  version once passed) — like the agenda document download. */}
              {yaumiyya.ninmunDocument && (
                <DocAction
                  icon="download"
                  label={downloading === 'ninmun' ? dv.common.downloading : dv.ninmun.download}
                  busy={downloading === 'ninmun'}
                  onClick={() => yaumiyya.ninmunDocument && void runDownload(
                    'ninmun',
                    yaumiyyaApi.ninmunDocumentUrl(yaumiyya.ninmunDocument.id),
                    `ninmun-${meeting.year}-${meeting.sequence}.pdf`,
                  )}
                  disabled={downloading === 'ninmun'}
                />
              )}
            </div>
            {ninmunSigning && (
              <p className="text-xs text-[hsl(var(--warning))]">{dv.ninmun.signingInProgress}</p>
            )}
            {downloadError && (
              <p className="text-xs text-destructive">{dv.common.error}</p>
            )}
          </section>
        )}

        {/* Haaziree (attendance register) — auto-generated when the yaumiyya is
            signed, then signed by the acting president via WapSign. Independent
            of the ninmun; same card pattern. */}
        {yStatus === 'APPROVED' && yaumiyya?.haazireeDocument && (
          <section className="space-y-3 rounded-3xl bg-[hsl(var(--chrome))] p-5 lg:p-6">
            <h2 className="section-heading status-label !mb-0 text-muted-foreground">{dv.haaziree.title}</h2>
            <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
              {haazireePassed ? <span className="pill pill-success">{dv.haaziree.signed}</span> : <span />}
              {yaumiyya.haazireeDocument?.generatedAt && (
                <span className="mono-meta text-muted-foreground" dir="ltr">
                  {formatDvDate(yaumiyya.haazireeDocument.generatedAt)} {formatDvTime(yaumiyya.haazireeDocument.generatedAt)}
                </span>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2 border-t border-black/[0.05] pt-2.5">
              {/* Not yet sent — SG/Bureau (or any writer) send it for signing,
                  then place the relevant councilor's signature box in WapSign. */}
              {!haazireePassed && !haazireeSignDocId && isWriter && (
                <DocAction
                  icon="send"
                  label={dv.haaziree.sendForSigning}
                  tone="filled"
                  onClick={() => sendHaazireeForSigning.mutate()}
                  disabled={sendHaazireeForSigning.isPending || opening}
                />
              )}
              {/* Sent, not yet passed — open in WapSign to sign. Hidden in
                  demo mode — nothing to open, signing already completed. */}
              {haazireeSigning && !user.isDemo && (
                <DocAction
                  icon="open_in_new"
                  label={dv.haaziree.openInWapsign}
                  onClick={() => haazireeSignDocId && void openInWapsign(haazireeSignDocId)}
                  disabled={opening}
                />
              )}
              {/* Download the haaziree PDF (the signed version once passed). */}
              {yaumiyya.haazireeDocument && (
                <DocAction
                  icon="download"
                  label={downloading === 'haaziree' ? dv.common.downloading : dv.haaziree.download}
                  busy={downloading === 'haaziree'}
                  onClick={() => void runDownload(
                    'haaziree',
                    yaumiyyaApi.haazireeFileUrl(meetingId),
                    `haaziree-${meeting.year}-${meeting.sequence}.pdf`,
                  )}
                  disabled={downloading === 'haaziree'}
                />
              )}
            </div>
            {haazireeSigning && (
              <p className="text-xs text-[hsl(var(--warning))]">{dv.haaziree.signingInProgress}</p>
            )}
            {downloadError && (
              <p className="text-xs text-destructive">{dv.common.error}</p>
            )}
          </section>
        )}
      </aside>{/* end side column */}
      </div>{/* end grid */}
    </div>
  );
}
